import { Compiler } from '@/dsl/compilers';
import type { UnifiedContext } from '../context';
import { isPresent } from './utilty';

const OPTION_PRICE_TOKENS = ['full', 'discounted', 'full-monthly', 'discounted-monthly'] as const;
type OptionPriceToken = typeof OPTION_PRICE_TOKENS[number];
type OptionCtx = NonNullable<UnifiedContext['option']>;

const STATE_PRICE_TOKENS = [
  'min-full',
  'max-full',
  'min-discounted',
  'max-discounted',
  'min-full-monthly',
  'max-full-monthly',
  'min-discounted-monthly',
  'max-discounted-monthly'
] as const;
type StatePriceToken = typeof STATE_PRICE_TOKENS[number];
type StateCtx = NonNullable<UnifiedContext['state']>;

const OPTION_PRICE_AVAILABLE = {
  'full'              : (option: OptionCtx) => isPresent(option.getPrice({ currency: false })),
  'discounted'        : (option: OptionCtx) => isPresent(option.getDiscountedPrice({ currency: false })),
  'full-monthly'      : (option: OptionCtx) => isPresent(option.getPrice({ currency: false, monthly: true })),
  'discounted-monthly': (option: OptionCtx) => isPresent(option.getDiscountedPrice({ currency: false, monthly: true }))
} satisfies Record<OptionPriceToken, (option: OptionCtx) => boolean>;

const OPTION_PRICE_RENDER = {
  'full'              : (option: OptionCtx) => option.getPrice(),
  'discounted'        : (option: OptionCtx) => option.getDiscountedPrice(),
  'full-monthly'      : (option: OptionCtx) => option.getPrice({ monthly: true }),
  'discounted-monthly': (option: OptionCtx) => option.getDiscountedPrice({ monthly: true })
} satisfies Record<OptionPriceToken, (option: OptionCtx) => string>;

const STATE_PRICE_AVAILABLE = {
  'min-full'              : (state: StateCtx) => Boolean(state.price.min.fmt),
  'max-full'              : (state: StateCtx) => Boolean(state.price.max.fmt),
  'min-full-monthly'      : (state: StateCtx) => Boolean(state.price.monthly.min.fmt),
  'max-full-monthly'      : (state: StateCtx) => Boolean(state.price.monthly.max.fmt),
  'min-discounted'        : (state: StateCtx) => Boolean(state.discountedPrice.min.fmt),
  'max-discounted'        : (state: StateCtx) => Boolean(state.discountedPrice.max.fmt),
  'min-discounted-monthly': (state: StateCtx) => Boolean(state.discountedPrice.monthly.min.fmt),
  'max-discounted-monthly': (state: StateCtx) => Boolean(state.discountedPrice.monthly.max.fmt)
} satisfies Record<StatePriceToken, (state: StateCtx) => boolean>;

const STATE_PRICE_RENDER = {
  'min-full'              : (state: StateCtx) => state.price.min.fmt,
  'max-full'              : (state: StateCtx) => state.price.max.fmt,
  'min-full-monthly'      : (state: StateCtx) => state.price.monthly.min.fmt,
  'max-full-monthly'      : (state: StateCtx) => state.price.monthly.max.fmt,
  'min-discounted'        : (state: StateCtx) => state.discountedPrice.min.fmt,
  'max-discounted'        : (state: StateCtx) => state.discountedPrice.max.fmt,
  'min-discounted-monthly': (state: StateCtx) => state.discountedPrice.monthly.min.fmt,
  'max-discounted-monthly': (state: StateCtx) => state.discountedPrice.monthly.max.fmt
} satisfies Record<StatePriceToken, (state: StateCtx) => string>;

// Handles both data-store-price (option-level) and data-store-context-price (state-level)
export const handlePrice = (el: HTMLElement, ctx: UnifiedContext) => {
  const { storePrice, storeContextPrice } = el.dataset;
  const option = ctx.option;

  // Option-level price
  if (storePrice && option) {
    const chosen = Compiler.enum<OptionPriceToken>({
      expr       : storePrice,
      allowed    : OPTION_PRICE_TOKENS,
      isAvailable: (token) => OPTION_PRICE_AVAILABLE[token](option)
    });

    if (chosen) {
      el.textContent = OPTION_PRICE_RENDER[chosen](option);
      return;
    }
  }

  const state = ctx.state;

  // State-level price
  if (storeContextPrice && state) {
    const chosen = Compiler.enum<StatePriceToken>({
      expr       : storeContextPrice,
      allowed    : STATE_PRICE_TOKENS,
      isAvailable: (token) => STATE_PRICE_AVAILABLE[token](state)
    });

    if (chosen) {
      el.textContent = STATE_PRICE_RENDER[chosen](state);
    }
  }
};
